import { useState } from 'react';
import {
  FacebookShareButton,
  TwitterShareButton,
  FacebookIcon,
  TwitterIcon,
  WhatsappShareButton,
  WhatsappIcon,
  LinkedinShareButton,
  LinkedinIcon,
} from 'react-share';
import { useTranslation } from 'react-i18next';
import { useMediaPredicate } from 'react-media-hook';
import { Share2 } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { Button } from './ui/button';

interface SocialShareProps {
  isLoading?: boolean;
  disabled?: boolean;
  content: string;
}

export default function SocialShare({
  isLoading,
  content,
  disabled = false,
}: SocialShareProps) {
  const { t } = useTranslation();

  const [isCopied, setIsCopied] = useState<boolean>(false);

  const onCopyContent = () => {
    navigator.clipboard.writeText(content);
    setIsCopied(true);

    setTimeout(() => setIsCopied(false), 2000);
  };

  const isMediaMobile = useMediaPredicate('(max-width: 640px)');

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant='outline'
            className='h-8 px-4 text-blue-500 border-blue-500 rounded-lg sm:rounded-full hover:text-blue-500'
            disabled={isLoading || disabled}
          >
            <Share2 className='w-4 h-4 sm:mr-2' />
            <span className='hidden sm:inline'>{t('share')}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align={isMediaMobile ? 'center' : 'end'}
          className='p-0 ml-4'
        >
          <DropdownMenuLabel className='mt-1 text-base'>
            {t('share_this_report')}
          </DropdownMenuLabel>
          <DropdownMenuSeparator className='bg-gray-200' />
          <div className='flex items-center px-4 py-2 gap-x-4'>
            <DropdownMenuItem className='p-2 bg-green-100 rounded-sm'>
              <WhatsappShareButton url={content} aria-label='Share on WhatsApp'>
                <WhatsappIcon size={18} round={true} />
              </WhatsappShareButton>
            </DropdownMenuItem>
            <DropdownMenuItem className='p-2 rounded-sm bg-sky-100'>
              <TwitterShareButton url={content} aria-label='Share on Twitter'>
                <TwitterIcon size={18} round={true} />
              </TwitterShareButton>
            </DropdownMenuItem>
            <DropdownMenuItem className='p-2 bg-blue-100 rounded-sm'>
              <FacebookShareButton url={content} aria-label='Share on Facebook'>
                <FacebookIcon size={18} round={true} />
              </FacebookShareButton>
            </DropdownMenuItem>
            <DropdownMenuItem className='p-2 rounded-sm bg-cyan-100'>
              <LinkedinShareButton url={content} aria-label='Share on LinkedIn'>
                <LinkedinIcon size={18} round={true} />
              </LinkedinShareButton>
            </DropdownMenuItem>
          </div>
          <DropdownMenuSeparator className='mb-0 bg-gray-200' />
          <div className='p-3 bg-gray-100'>
            <div className='relative flex p-2 pb-1 bg-white rounded-md'>
              <input
                value={content}
                onChange={() => {}}
                className='w-48 h-8 pr-2 text-sm outline-none lg:w-60 font-isidora'
              />
              <Button
                variant='ghost'
                className='text-sm font-medium bg-gray-100 rounded-sm w-14 lg:w-20 h-7'
                onClick={onCopyContent}
              >
                {isCopied ? t('copied') : t('copy')}
              </Button>
            </div>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
